/**
 * Per-mission file-upload field state.
 *
 * Schema (the slot) lives on the Doc as a `file-upload` block.
 * Values live on the mission: `task.docFiles[docId][blockId] = FileRef[]`
 * (one file per slot; the array is 0-or-1).
 * Same copy-on-write idea as checkbox ticks — the doc body is never written.
 */

import { sanitizeThumbDataUrl } from './imageThumbnail';

export const DOC_UPLOAD_NOTE_PREFIX = 'doc-upload:';

export const inferFileType = (mimeType = '', filename = '', fileType = '') => {
  if (fileType) return fileType;
  const mime = String(mimeType || '').toLowerCase();
  const name = String(filename || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) {
    return 'image';
  }
  if (mime.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(name)) return 'video';
  if (mime.startsWith('audio/') || /\.(mp3|wav|ogg)$/i.test(name)) return 'audio';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return 'file';
};

export const makeDocUploadNote = (docId, blockId) =>
  `${DOC_UPLOAD_NOTE_PREFIX}${docId}:${blockId}`;

export const isDocUploadNote = (note) =>
  typeof note === 'string' && note.startsWith(DOC_UPLOAD_NOTE_PREFIX);

export const isDocUploadAttachment = (attachment) =>
  isDocUploadNote(attachment?.note);

export const toFileRef = (uploaded) => {
  if (!uploaded?.attachmentId) return null;
  const filename = uploaded.filename || 'Untitled';
  const mimeType = uploaded.mimeType || '';
  const fileType = inferFileType(mimeType, filename, uploaded.fileType || '');
  const thumbDataUrl = sanitizeThumbDataUrl(uploaded.thumbDataUrl);
  return {
    attachmentId: uploaded.attachmentId,
    filename,
    mimeType,
    size: typeof uploaded.size === 'number' ? uploaded.size : 0,
    fileType,
    uploadedAt: uploaded.uploadedAt || new Date().toISOString(),
    ...(thumbDataUrl ? { thumbDataUrl } : {}),
  };
};

export const normalizeFileList = (value) => {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      if (!item.attachmentId) return null;
      const filename = item.filename || 'Untitled';
      const mimeType = item.mimeType || '';
      const thumbDataUrl = sanitizeThumbDataUrl(item.thumbDataUrl);
      return {
        attachmentId: String(item.attachmentId),
        filename,
        mimeType,
        size: typeof item.size === 'number' ? item.size : 0,
        fileType: inferFileType(mimeType, filename, item.fileType || ''),
        uploadedAt: item.uploadedAt || '',
        ...(thumbDataUrl ? { thumbDataUrl } : {}),
      };
    })
    .filter(Boolean);
};

export const cloneFileOverrides = (overrides) => {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return {};
  }
  const next = {};
  for (const [blockId, files] of Object.entries(overrides)) {
    const list = normalizeFileList(files);
    if (list.length) next[blockId] = list;
  }
  return next;
};

export const fileOverridesEqual = (a, b) =>
  JSON.stringify(cloneFileOverrides(a)) === JSON.stringify(cloneFileOverrides(b));

export const readDisplayedFiles = (blockId, overrides) =>
  normalizeFileList(overrides?.[blockId]);

export const setBlockFiles = (overrides, blockId, files) => {
  const next = cloneFileOverrides(overrides);
  const list = normalizeFileList(files);
  if (list.length === 0) delete next[blockId];
  else next[blockId] = list;
  return next;
};

/** Count uploaded files across mission `docFiles` bags. */
export const countMissionDocFiles = (tasks = []) => {
  let fileCount = 0;
  let slotCount = 0;
  for (const task of tasks) {
    const bag =
      task?.docFiles && typeof task.docFiles === 'object' && !Array.isArray(task.docFiles)
        ? task.docFiles
        : {};
    for (const byBlock of Object.values(bag)) {
      if (!byBlock || typeof byBlock !== 'object' || Array.isArray(byBlock)) continue;
      for (const files of Object.values(byBlock)) {
        const list = normalizeFileList(files);
        if (list.length === 0) continue;
        slotCount += 1;
        fileCount += list.length;
      }
    }
  }
  return { fileCount, slotCount };
};

export const cloneDocFilesBag = (docFiles) => {
  if (!docFiles || typeof docFiles !== 'object' || Array.isArray(docFiles)) return {};
  const next = {};
  for (const [docId, overrides] of Object.entries(docFiles)) {
    const cloned = cloneFileOverrides(overrides);
    if (Object.keys(cloned).length) next[docId] = cloned;
  }
  return next;
};
